<template>
  <div class="survey-editor-page p-3">
    <b-overlay :show="isLoading" rounded="sm" spinner-variant="primary" spinner-type="grow">

      <b-card class="border-info">


        <b-modal id="importExcelModel" size="xl" v-model="viewImportExcelModel" title="" hide-footer>
          <div class="excelImport">
            <div class="loading" v-if="fetchData">
              <load-data></load-data>
            </div>

            <b-container class="bg-light" v-else>
              <b-row>
                <b-col md="12">
                  <!-- Close Button -->
                  <div class="closeBtn mb-2 text-right">
                    <p @click="viewImportExcelModel = false" class="btn m-0">
                      <i class="fas fa-window-close fa-2x text-danger"></i>
                    </p>
                  </div>

                  <!-- Modal Title -->
                  <h5 class="text-primary text-center mb-3">
                    Import Questions for: <span class="text-info">{{ survey.name }}</span>
                  </h5>

                  <!-- File Selection -->
                  <div class="selectExcelFile mb-3">
                    <div class="d-flex align-items-center justify-content-center">
                      <b-form-file ref="selectedExcelFile" style="display: none" v-model="excelSheet"
                        accept=".xlsx"></b-form-file>

                      <b-button v-if="!excelSheet" variant="info" pill size="md"
                        @click="$refs.selectedExcelFile.$refs.input.click()">
                        <i class="fas fa-file-excel mr-2"></i>Select Excel File
                      </b-button>

                      <h6 v-else class="text-dark font-weight-bolder ml-3">
                        {{ excelSheet.name }}
                        <span class="text-danger btn m-0 p-0" @click="removeExcelSheet()">
                          <i class="fas fa-trash ml-2"></i>
                        </span>
                      </h6>

                      <b-button v-if="surveyQuestions.length == 0" variant="outline-success" class="ml-3"
                        @click="checkExcelSheet" :disabled="!excelSheet || surveyQuestions.length > 0">
                        <i class="fas fa-upload fa-2x ml-2"></i>
                      </b-button>
                    </div>
                  </div>

                  <!-- Sample Image / Note -->
                  <div class="sampleCsv mb-3" v-if="surveyQuestions.length == 0">
                    <p class="text-info">
                      <b class="text-dark mr-2">Note: </b>
                      While preparing the Excel sheet, include only Question number,
                          Question, Options, Scores. Headers are not required.
                      <br />
                      Refer to the sample image below:
                    </p>
                    <img :src="surveyExcelImg" alt="" class="img-fluid" />
                  </div>

                  <!-- Progress Bar -->
                  <div class="progressBar mb-3" v-if="loadData">
                    <b-progress :value="value" :max="max" show-progress animated variant="success"></b-progress>
                  </div>

                  <!-- Survey Questions List -->
                  <div class="surveyQuestions" v-if="surveyQuestions.length != 0">
                    <b-form @submit.prevent="prepareSurveyQuestionsForAPI()">
                      <div class="question border border-info rounded mt-2 p-3 bg-light"
                        v-for="(question, questionIndex) in surveyQuestions" :key="questionIndex">
                        <b-overlay :show="loadQuestion" spinner-variant="primary" spinner-type="grow" spinner-small
                          rounded="md">
                          <!-- Question Number and Delete -->
                          <div class="d-flex justify-content-between">
                            <b-form-input style="width: 100px; text-align: center" v-model="question.question_no"
                              :disabled="question.disabled" placeholder="Q No."></b-form-input>
                            <span class="btn p-0 m-0 text-danger" @click="surveyQuestions.splice(questionIndex, 1)">
                              <i class="fas fa-minus-circle"></i>
                            </span>
                          </div>

                          <!-- Question Text -->
                          <div class="mt-2">
                            <b-form-input placeholder="Question" v-model="question.name"
                              :disabled="questionSubmit.disabled" class="text-center"></b-form-input>
                          </div>

                          <!-- Choices and Scores -->
                          <div class="mt-2">
                            <b-form-input placeholder="Enter options separated by commas" v-model="question.choices"
                              :disabled="questionSubmit.disabled" class="text-center"></b-form-input>
                            <b-form-input placeholder="Enter scores separated by commas" v-model="question.scores"
                              :disabled="questionSubmit.disabled" class="text-center mt-2"></b-form-input>
                          </div>

                          <!-- Course Outcomes -->
                          <div class="mt-2" v-if="survey.survey_type == 'course_exit'">
                            <multiselect v-model="question.course_outcomes" :options="courseOutcomesOptions"
                              :multiple="true" :close-on-select="false" :clear-on-select="false"
                              placeholder="Select Course outcomes" label="text" track-by="value"></multiselect>
                          </div>

                          <!-- PO/PSO -->
                          <div class="mt-2" v-if="survey.survey_type == 'programme_exit'">
                            <multiselect v-model="question.programme_outcomes" :options="poOptions" :multiple="true"
                              :close-on-select="false" :clear-on-select="false" placeholder="Select Programme outcomes"
                              label="text" track-by="value" :disabled="question.programme_specificoutcomes.length > 0">
                            </multiselect>
                            <multiselect v-model="question.programme_specificoutcomes" :options="psoOptions"
                              :multiple="true" :close-on-select="false" :clear-on-select="false"
                              placeholder="Select Programme specific outcomes" label="text" track-by="value"
                              :disabled="question.programme_outcomes.length > 0"></multiselect>
                          </div>

                          <!-- Allow Text / Mandatory -->
                          <div class="mt-2 d-flex justify-content-center">
                            <b-form-checkbox v-model.trim="question.allow_descriptive"
                              :disabled="questionSubmit.disabled">Allow Text Field</b-form-checkbox>
                            <b-form-checkbox v-model.trim="question.answer_required" class="ml-3"
                              :disabled="questionSubmit.disabled">Answer Mandatory</b-form-checkbox>
                          </div>
                        </b-overlay>
                      </div>

                      <!-- Submit Button -->
                      <div class="text-center mt-3">
                        <b-button type="submit" variant="success" :disabled="questionSubmit.disabled">
                          {{ questionSubmit.submit }}
                        </b-button>
                      </div>
                    </b-form>
                  </div>
                </b-col>
              </b-row>
            </b-container>
          </div>
        </b-modal>



        <!-- Header -->
        <div class="d-flex align-items-center mb-2">
          <!-- Import Excel button on the left -->
          <b-button v-if="!usersubgroup_id" variant="primary" v-b-modal.importExcelModel>
            Import Excel
          </b-button>

          <!-- Centered Title -->
          <div class="flex-grow-1 text-center">
            <h5 class="text-primary mb-0">{{ survey.name }} - Questions</h5>
          </div>
        </div>

        <!-- Top Action Buttons -->
        <div class="text-center mb-3" v-if="!usersubgroup_id">
          <b-button variant="primary" size="sm" @click="addQuestion">Add Question</b-button>
          <b-button variant="success" size="sm" class="ml-2" @click="saveQuestions(false)">Save All</b-button>
          <b-button variant="danger" size="sm" class="ml-2" @click="closeTab">Close</b-button>
        </div>

        <!-- Question Form -->
        <b-form @submit.prevent="saveQuestions(false)" v-if="survey.questions && survey.questions.length > 0">
          <!-- Questions List -->
          <div v-for="(question, qIndex) in survey.questions" :key="qIndex"
            class="border border-info rounded mt-2 p-3 bg-light">
            <!-- View Mode -->
            <div v-if="!question.editMode">
              <div class="question d-flex align-items-start">
                <div class="questionNo">
                  <p style="font-size: 16px; font-weight: 600;">{{ question.question_no }}:</p>
                </div>

                <div class="question ml-2 w-100">
                  <p style="font-size: 16px; font-weight: 600;">{{ question.name }}</p>

                  <!-- Choices -->
                  <div v-if="question.choices.length">
                    <h6 style="font-size: 16px; font-weight: 600;">Choices:</h6>
                    <p v-for="(c, ci) in question.choices" :key="ci"
                      style="font-size: 12px; font-weight: 600;"
                      class="bg-info p-1 text-white rounded ml-2 d-inline-block">
                      {{ c }}
                    </p>
                  </div>

                  <!-- Course Outcomes -->
                  <div v-if="question.course_outcomes.length" class="mt-1">
                    <h6 style="font-size: 14px; font-weight: 600;">Course Outcomes:</h6>
                    <p v-for="co in question.course_outcomes" :key="co.value"
                      class="bg-primary p-1 text-white rounded ml-2 d-inline-block"
                      style="font-size: 12px; font-weight: 600;">
                      {{ co.text }}
                    </p>
                  </div>

                  <!-- Programme Outcomes -->
                  <div v-if="question.programme_outcomes.length" class="mt-1">
                    <h6 style="font-size: 14px; font-weight: 600;">Programme Outcomes:</h6>
                    <p v-for="po in question.programme_outcomes" :key="po.value"
                      class="bg-success p-1 text-white rounded ml-2 d-inline-block"
                      style="font-size: 12px; font-weight: 600;">
                      {{ po.text }}
                    </p>
                  </div>

                  <!-- Programme Specific Outcomes -->
                  <div v-if="question.programme_specificoutcomes.length" class="mt-1">
                    <h6 style="font-size: 14px; font-weight: 600;">Programme Specific Outcomes:</h6>
                    <p v-for="pso in question.programme_specificoutcomes" :key="pso.value"
                      class="bg-success p-1 text-white rounded ml-2 d-inline-block"
                      style="font-size: 12px; font-weight: 600;">
                      {{ pso.text }}
                    </p>
                  </div>

                  <!-- Allow Text / Mandatory -->
                  <div class="questionOptions d-flex justify-content-start mt-2">
                    <div>
                      <b-form-checkbox size="sm" v-model.trim="question.allow_descriptive" disabled>
                        <span style="font-size: 12px; font-weight: 600;"
                          :class="question.allow_descriptive ? 'text-success' : 'text-danger'">
                          Allow Text Field
                        </span>
                      </b-form-checkbox>
                    </div>
                    <div class="ml-3">
                      <b-form-checkbox size="sm" v-model.trim="question.answer_required" disabled>
                        <span style="font-size: 12px; font-weight: 600;"
                          :class="question.answer_required ? 'text-success' : 'text-danger'">
                          Answer Mandatory
                        </span>
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="ml-auto d-flex align-items-center" v-if="!usersubgroup_id">
                  <b-button variant="info" size="sm" class="m-2" @click="editQuestion(qIndex)">Edit</b-button>
                  <span @click="deleteQuestion(qIndex)" class="text-danger btn m-0 p-0">
                    <i class="fas fa-trash ml-1"></i>
                  </span>
                </div>
              </div>
            </div>

            <!-- Edit Mode -->
            <div v-else>
              <div class="d-flex align-items-center">
                <b-form-input v-model="question.question_no" placeholder="Q No."
                  style="width:80px; text-align:center;" class="mr-2"></b-form-input>
                <b-form-input v-model="question.name" placeholder="Enter Question"
                  class="flex-grow-1"></b-form-input>
                <b-button size="sm" variant="success" class="ml-2" @click="saveQuestion(qIndex)">Save</b-button>
                <span class="btn p-0 m-0 text-danger" @click="deleteQuestion(qIndex)">
                  <i class="fas fa-minus-circle"></i>
                </span>
              </div>

              <div class="mt-2">
                <b-form-tags v-model="question.choices" tag-variant="info" tag-pills size="md" separator=",;"
                  placeholder="Enter Option-Score pair separated by commas"
                  :tag-validator="validateOptionPattern"></b-form-tags>
              </div>

              <div class="mt-2" v-if="survey.key === 'course_exit'">
                <multiselect v-model="question.course_outcomes" :options="courseOutcomesOptions" :multiple="true"
                  label="text" track-by="value" placeholder="Select Course outcomes"
                  :close-on-select="false" />
              </div>

              <div class="mt-2"
                v-if="['programme_exit','alumini','employer','parent','faculty'].includes(survey.key)">
                <multiselect v-model="question.programme_outcomes" :options="poOptions" :multiple="true"
                  label="text" track-by="value" placeholder="Select Programme outcomes"
                  :close-on-select="false"
                  :disabled="question.programme_specificoutcomes && question.programme_specificoutcomes.length > 0" />

                <multiselect v-model="question.programme_specificoutcomes" :options="psoOptions" :multiple="true"
                  label="text" track-by="value" placeholder="Select Programme specific outcomes"
                  :close-on-select="false"
                  :disabled="question.programme_outcomes && question.programme_outcomes.length > 0" />
              </div>

              <div class="mt-2 d-flex justify-content-center">
                <b-form-checkbox v-model.trim="question.allow_descriptive">Allow Text Field</b-form-checkbox>
                <b-form-checkbox v-model.trim="question.answer_required" class="ml-3">Answer Mandatory</b-form-checkbox>
              </div>
            </div>
          </div>

          <!-- Bottom Action Buttons -->
          <div class="mt-3 text-center" v-if="!usersubgroup_id">
            <b-button variant="primary" size="sm" @click="addQuestion">Add Question</b-button>
            <b-button variant="success" size="sm" class="ml-2" @click="saveQuestions(false)">Save All</b-button>
            <b-button variant="danger" size="sm" class="ml-2" @click="closeTab">Close</b-button>
          </div>
        </b-form>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import surveyExcelImg from '@/assets/images/surveyExcel.png';
import readXlsxFile from "read-excel-file";
export default {
  data() {
    return {
      usersubgroup_id: '',
      surveyExcelImg,
      survey: {},
      surveys: [
        // { name: "Instructor Feedback Survey", key: "instructor_feedback", set_as_default: false, questions: [] },
        { name: "Programme Exit Survey", key: "programme_exit", set_as_default: false, questions: [] },
        // { name: "Course Exit Survey", key: "course_exit", set_as_default: false, questions: [] },
        { name: "Faculty Survey", key: "faculty", set_as_default: false, questions: [] },
        { name: "Alumni Survey", key: "alumini", set_as_default: false, questions: [] },
        { name: "Employer Survey", key: "employer", set_as_default: false, questions: [] },
        { name: "Parent Survey", key: "parent", set_as_default: false, questions: [] },
        // { name: "HoD Feedback Survey", key: "hod_feedback", set_as_default: false, questions: [] },
        // { name: "Class Advisors Feedback Survey", key: "class_advisors_feedback", set_as_default: false, questions: [] },
        // { name: "Infrastructure Feedback Survey", key: "infrastructure_feedback", set_as_default: false, questions: [] },
      ],
      poOptions: [],
      psoOptions: [],
      courseOutcomesOptions: [],
      isLoading: false, //  loader state
      viewImportExcelModel: false,
      importExcelFile: null,
      excelImportStatus: "",


      fetchData: false,
      loadQuestion: false,

      excelSheet: null,
      surveyQuestionsDataFromExcel: [],
      surveyQuestions: [],
      loadData: false,
      value: 50,
      max: 100,
      questionSubmit: { submit: "Submit", disabled: false },

    };
  },

  async created() {
    this.usersubgroup_id = this.$route.params.usersubgroup_id != 0 ?this.$route.params.usersubgroup_id:'';
    this.isLoading = true;
    try {
      await this.getPoAndPos();
      await this.loadAllSurveys();
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    async prepareSurveyQuestionsForAPI() {
      try {
        // Ensure there are questions from Excel
        if (this.surveyQuestions.length === 0) {
          this.$toast.error("Please import at least one question before submitting.", {
            position: "top",
            duration: 3000
          });
          return;
        }

        // Merge imported Excel questions into the current survey
        this.survey.questions.push(...this.surveyQuestions);

        // Save using your existing saveQuestions() logic
        await this.saveQuestions(false);

        // Close modal after save
        this.viewImportExcelModel = false;

        // Reset Excel form
        this.excelSheet = null;
        this.surveyQuestions = [];

        this.$toast.success("Excel questions imported and saved successfully!", {
          position: "top",
          duration: 3000
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error("Excel import failed:", error);
        this.$toast.error("Failed to import Excel questions.", {
          position: "top",
          duration: 3000
        });
      }
    },


    removeExcelSheet() {
      this.excelSheet = null;
      this.surveyQuestions = [];
      this.surveyQuestionsDataFromExcel = [];
    },
    async checkExcelSheet() {
      this.loadData = true;
      const rows = await readXlsxFile(this.excelSheet);
      this.surveyQuestionsDataFromExcel = rows;

      rows.forEach((row) => {
        this.surveyQuestions.push({
          question_no: row[0],
          name: row[1],
          choices: row[2],
          scores: row[3],
          allow_descriptive: false,
          answer_required: true,
          survey_id: this.survey.id,
          survey_model: "StudFBSurvey",
          course_outcomes: [],
          programme_outcomes: [],
          programme_specificoutcomes: [],
          from_excel: true
        });
      });
      this.loadData = false;
      this.value = 50;
    },

    closeTab() {
      window.close();
    },

    async loadAllSurveys() {
      const group_id = this.$route.params.group_id;
      const survey_index = this.$route.params.survey_index;
      const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${group_id}/survey_default_questions`;
      try {
        const response = await this.$axios.get(url);
        const data = response.data || {};

        this.surveys.forEach(s => {
          const existingQuestions = data[s.key]?.questions || [];
          s.questions = existingQuestions.map((q, i) => ({
            question_no: q.question_no ?? i + 1,
            name: q.name ?? "",
            allow_descriptive: q.allow_descriptive ?? false,
            answer_required: q.answer_required ?? false,
            choices: q.choices ?? [],
            course_outcomes: q.course_outcomes ?? [],
            programme_outcomes: q.programme_outcomes ?? [],
            programme_specificoutcomes: q.programme_specificoutcomes ?? [],
            editMode: false,
          }));
          s.set_as_default = data[s.key]?.set_as_default ?? false;
        });

        this.survey = this.surveys[survey_index];
      } catch (error) {
        console.error("Failed to load surveys", error);
      }
    },

    editQuestion(qIndex) {
      this.survey.questions[qIndex].editMode = true;
    },

    addQuestion() {
      this.survey.questions.push({
        question_no: this.survey.questions.length + 1,
        name: "",
        allow_descriptive: false,
        answer_required: true,
        choices: [],
        course_outcomes: [],
        programme_outcomes: [],
        programme_specificoutcomes: [],
        editMode: true,
      });
    },

    deleteQuestion(qIndex) {
      this.survey.questions.splice(qIndex, 1);
      this.saveQuestions(true);
    },
    saveQuestion(qIndex) {
      const question = this.survey.questions[qIndex];

      // Validate: Choices required if not descriptive
      if (!question.allow_descriptive && (!question.choices || !question.choices.length)) {
        this.$toast.error('Please enter at least one option for this question.', { position: "top", duration: 3000 });
        return;
      }

      question.editMode = false;
      this.saveQuestions(false);
    },

    async saveQuestions(is_delete) {
      // Validate all questions
      for (let q of this.survey.questions) {
        if (!q.allow_descriptive && (!q.choices || !q.choices.length)) {
          this.$toast.error(`Question ${q.question_no} must have at least one option.`, {
            position: "top",
            duration: 3000,
          });
          return;
        }
      }

      try {
        // 🔹 Merge choices and scores if imported from Excel
        this.survey.questions = this.survey.questions.map((q) => {
          if (q.from_excel) {
            const choices = q.choices ? q.choices.split(",") : [];
            const scores = q.scores ? q.scores.split(",") : [];

            // merge "option-score"
            const mergedChoices = choices.map((choice, index) => {
              const score = scores[index] !== undefined ? scores[index] : "";
              return `${choice.trim()}-${score.trim()}`;
            });

            // return transformed question
            return {
              ...q,
              choices: mergedChoices,
            };
          }
          return q; // unchanged if not from Excel
        });

        const group_id = this.$route.params.group_id;
        const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups-configuration-groups/setting/${group_id}/survey_default_questions`;

        const surveyData = {};
        this.surveys.forEach((s) => {
          surveyData[s.key] = {
            set_as_default: s.set_as_default,
            questions: s.questions,
          };


        });

        await this.$axios.post(url, { survey_default_questions: surveyData });

        // Exit edit mode
        this.survey.questions.forEach((q) => (q.editMode = false));

        if (is_delete) {
          this.$toast.success("Questions Deleted successfully!", {
            position: "top",
            duration: 3000,
          });
        }
        else {
          this.$toast.success("Questions Updated successfully!", {
            position: "top",
            duration: 3000,
          });
        }
        
      } catch (error) {
        console.error("Failed to save questions", error);
        this.$toast.error("Failed to save questions", {
          position: "top",
          duration: 3000,
        });
      }
    },


    validateOptionPattern(pattern) {
      // Simple Option-Score validation: e.g., Option-1
      return /^.+-\d+$/.test(pattern.trim());
    },

    async getPoAndPos() {
      await this.getProgramOutcomes();
      await this.getProgramSpecificOutcomes();
    },

    async getProgramOutcomes() {
      const url = `${this.$store.getters.getAPIKey.programOutcome}?usergroup=${this.$route.params.usergroup_id}`;
      const res = await this.$axios.get(url);
      this.poOptions = res.data.map(po => ({ value: po.id, text: po.code }));
    },

    async getProgramSpecificOutcomes() {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroup/${this.$route.params.usergroup_id}/programmespecificoutcomes`;
      const res = await this.$axios.get(url);
      this.psoOptions = res.data.map(pso => ({ value: pso.id, text: pso.code }));
    }
  }
};
</script>
<style>
.excelImport .container {
  min-height: 70vh;
  padding: 10px;
}
</style>
